/**
* TextWrap.js
* 
* Created on 16-okt-2014, 9:16:42
*
* @author Harm Boschloo for Ranj
**/

import { isNull } from '../../util/TypeUtil';

export default class TextWrap {
    /**
     * Wraps words on whitespaces and splits a word when it is too log for a line.
     * 
     * @param {string} text 
     * @param {number} width 
     * @param {CanvasRenderingContext2D} context 
     * @param {boolean} returnLines true if should return lines in an Array; false, if it should be combined in one string
     * @param {number} maxLines 
     * @returns {string|Array<string>} text with breaklines on wrapping positions, or array with lines
     */
    static wrapText(text, width, context, returnLines, maxLines) {
        if (arguments.length <= 3) { returnLines = false; }
        
        var lines = String(text).split(/(?:\r\n|\r|\n)/);
        var newLines = [];
        
        for (var i = 0; i < lines.length; ++i) {
            var line = lines[i];
            var lineWidth = context.measureText(line).width;
            
            if (lineWidth > width) {
                // split words on whitespaces; returns whitespaces as well
                var parts = line.split(/(\s)/);
                
                var linePart = "";
                var newLinePart = "";
                var newLineWidth = 0;
                for (var j = 0; j < parts.length; ++j) {
                    var part = parts[j];
                    newLinePart = linePart + part;
                    newLineWidth = context.measureText(newLinePart).width;
                    
                    if (newLineWidth > width) {
                        if (linePart === "") {
                            // word too big for line, split word
                            var wordParts = TextWrap._splitWord(newLinePart, width, context);
                            for (var k = 0; k < (wordParts.length-1); ++k) {
                                newLines.push(wordParts[k]);
                                if (maxLines && newLines.length >= maxLines) { break; }
                            }
                            // insert last word part into parts
                            parts.insert(j+1, wordParts[wordParts.length-1]);
                        } else {
                            // add new line
                            newLines.push(linePart);
                            if (maxLines && newLines.length >= maxLines) { break; }
                            // redo this part for the next line
                            --j;
                            linePart = "";
                            // TODO remove leading whitespace (1) for next line if any
                        }
                    } else {
                        linePart = newLinePart;
                    }
                }
                newLines.push(newLinePart);
            }
            else {
                newLines.push(line);
            }
            
            if (maxLines && newLines.length >= maxLines) { break; }
        }
        
        return returnLines ? newLines : TextWrap._combineLines(newLines);
    };
    
    // fixes createjs wrapping not splitting long words
    // disables createjs wrapping by setting textField.lineWidth = null

    /**
     * Wraps text from createjs.Text by splitting long words with breaklines 
     * 
     * NB: createjs.Text wrapping only works with whitespaces.
     * 
     * @param {createjs.Text} textField 
     * @param {number} lineWidth 
     * @param {number} maxLines 
     * @param {string} truncateCharacters string to use on truncating if maxLines are provided
     */
    static wrapTextField (textField, lineWidth, maxLines, truncateCharacters) {
        if (arguments.length <= 1) {
            lineWidth = textField.lineWidth || textField.oldLineWidth;
            if (isNull(lineWidth)) {
                console.error("lineWidth or textField.lineWidth not provided");
                return;
            }
        }
        // TODO have to hack the context here
        var context = textField._prepContext(createjs.Text._workingContext);
        if (maxLines && maxLines > 0) {
            var lines = TextWrap.wrapText(textField.text, lineWidth, context, true, maxLines);
            if (lines.length > maxLines) {
                lines = lines.slice(0, maxLines);
                truncateCharacters = truncateCharacters || "...";
                lines[maxLines-1] = lines[maxLines-1].slice(0, -truncateCharacters.length) + truncateCharacters;
            }
            textField.text = TextWrap._combineLines(lines);
        } else {
            textField.text = TextWrap.wrapText(textField.text, lineWidth, context);
        }
        // store line width and prevent createjs wrapping
        textField.oldLineWidth = lineWidth;
        textField.lineWidth = null;
    }
    
    /**
     * 
     * @param {string} word 
     * @param {number} width 
     * @param {CanvasRenderingContext2D} context 
     * @returns {Array<string>} split word into each array index
     */
    static _splitWord (word, width, context) {
        var wordParts = [];
        
        while(context.measureText(word).width > width) {
            var splitSize = Math.floor(word.length/2);
            var splitIndex = splitSize;
            var part = "";
            var partWidth = 0;
            var tooBig = true;
            var wasTooBig = true;
            while(true) {
                part = word.substring(0, splitIndex);
                partWidth = context.measureText(part).width;
                wasTooBig = tooBig;
                tooBig = partWidth > width;
                if (splitSize > 1) { 
                    splitSize = Math.floor(splitSize/2);
                    if (splitSize < 1) { splitSize = 1; }
                    splitIndex += tooBig ? -splitSize : splitSize;
                }
                else {
                    if (wasTooBig !== tooBig) {
                        if (tooBig) {
                            --splitIndex;
                            part = word.substring(0, splitIndex);
                        }
                        break;
                    } 
                    else {
                        splitIndex += tooBig ? -1 : 1;
                    }
                }
            }
            
            wordParts.push(part);
            word = word.substring(splitIndex);
        }
        
        if (word !== "") {
            wordParts.push(word);
        }
        
        return wordParts;
    }
    
    /**
     * 
     * @param {Array<string>} lines 
     * @returns {string}
     */
    static _combineLines(lines) {
        var text = "";
        for (var i = 0; i < lines.length; ++i) {
            text += ((i === 0 ? "" : "\n") + lines[i]);
        }
        return text;
    }
}